import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import { Button, Header, UnlockCodeModal } from '../components';
import { FillingSpinner } from '../components/FillingSpinner';
import ClosedKiosk from '../components/ClosedKiosk';
import ErrorContainer from '../components/ErrorContainer';
import ButtonContainer from '../components/ButtonContainer';
import useUpdatingKioskState from '../hooks/useUpdatingKioskState';
import useInactivityLogout from '../hooks/useInactivityLogout';
import kioskUtils from '../utils/kiosk';
import { isMobileDevice, showLogoutButton } from '../utils';
import { CREDENTIALS, getLoginType, logout } from '../resources/auth';
import { isEmpty } from 'lodash';
import { FlexContainer, Wrapper } from '../ui/StyledComponents';
import { LockOutlined } from '@ant-design/icons';

const ParkingProduct = ({ prod, disabled, onSelectProduct }) => {
  if (!prod?.uid) {
    return null;
  }
  return <div>
    <Button
      fullwidth="true"
      className="btn-product"
      disabled={disabled}
      type="primary"
      size="large"
      style={{ fontWeight: '700' }}
      onClick={() => onSelectProduct(prod)}
    >
      {prod.requiresUnlockCode && <LockOutlined style={{ marginRight: '8px', flex: 0 }} />}
      {prod.name}
    </Button>
  </div>;
};

const BackOrLogoutButton = ({ kioskState, onClose }) => {
  const { t } = useTranslation();
  const products = nonRepeatedParkingLocations();
  const showBackButton = products.length > 1 && kioskState.kiosk.parkingLocationSelection;

  if (showBackButton) {
    return (
      <Button disabled={!kioskState.connectionOk} className="backButton" onClick={() => onClose()}>
        {t('back')}
      </Button>
    );
  } else if (showLogoutButton()) {
    return (
      <Button disabled={!kioskState.connectionOk} className="backButton" onClick={logout}>
        {t('logout')}
      </Button>
    );
  }
  return null;

  function nonRepeatedParkingLocations() {
    const uniquePlaceUids = new Set();
    return (kioskState?.kiosk?.kioskProducts ?? []).filter(({ uid, placeUid }) => {
      const placeKey = placeUid || uid;
      if (uniquePlaceUids.has(placeKey)) {
        return false;
      }
      uniquePlaceUids.add(placeKey);
      return true;
    });
  }
};

const ParkingProductSelection = ({ location: { state }, history, setKioskState }) => {
  const { t } = useTranslation();
  const [ kioskState ] = useUpdatingKioskState();
  const [ isAvailable, setIsAvailable ] = useState(false);
  const [ unlockModalVisible, setUnlockModalVisible ] = useState(false);
  const [ selectedProduct, setSelectedProduct ] = useState(null);

  const { parkingLocation } = state;

  useEffect(() => {
    setKioskState(kioskState);
  }, [ kioskState ]);

  const logoutActive = !isMobileDevice() && (getLoginType() === CREDENTIALS);
  useInactivityLogout(logoutActive);

  const products = kioskUtils.getVisibleProducts(kioskState.kiosk);

  // Show error if no products are shown or all the visible products are disabled
  const showNoActiveProductsError = () => {
    return products?.length === 0 ||
      products?.every(product => kioskUtils.invalidProduct(product));
  };

  const onClose = () => {
    history.replace('/parking-locations');
  };

  const handleProductSelection = product => {
    if (product.requiresUnlockCode) {
      setSelectedProduct(product);
      setUnlockModalVisible(true);
    } else {
      navigateToRegistration(product.uid);
    }
  };

  const handleUnlockSuccess = unlockCode => {
    setUnlockModalVisible(false);
    if (selectedProduct) {
      navigateToRegistration(selectedProduct.uid, unlockCode);
    }
  };

  const handleUnlockClose = () => {
    setUnlockModalVisible(false);
    setSelectedProduct(null);
  };

  const navigateToRegistration = (productId, unlockCode = null) => {
    history.push('/register', {
      selectedProductId: productId,
      isMultiProductKiosk: true,
      unlockCode,
    });
  };

  useEffect(() => {
    // Check whether current time is inside opening hours
    setIsAvailable(kioskUtils.isKioskAvailable(kioskState.kiosk));
  }, [ kioskState ]);

  // Loading kiosk data
  if (!kioskState.isFetched) {
    return <FillingSpinner />;
  }

  // Closed kiosk
  if (kioskUtils.hasAvailabilityTimes(kioskState.kiosk) && !isAvailable) {
    return (
      <ErrorContainer>
        <ClosedKiosk kiosk={kioskState.kiosk}/>
      </ErrorContainer>);
  }

  // No service channel referralcodes
  if (isEmpty(kioskState.kiosk?.serviceChannelReferralCode)) {
    return (
      <ErrorContainer>
        <Alert
          type="error"
          message={t('error.noEntity', { entity: t('serviceChannelReferralCode') })} />
      </ErrorContainer>);
  }

  // No active valid products
  if ( showNoActiveProductsError()) {
    return (
      <ErrorContainer>
        <Alert
          type="error"
          message={t('error.noEntity', { entity: t('product') })} />
      </ErrorContainer>);
  }
  const applicableProducts = kioskState.kiosk.parkingLocationSelection ?
    products.filter(prod => prod.placeUid === parkingLocation) :
    products;

  // If there is only one product and it is not locked, redirect to registration
  if (applicableProducts.length === 1) {
    const product = applicableProducts[0];
    if (!kioskUtils.invalidProduct(product) && !product.requiresUnlockCode) {
      return <Redirect to={{ pathname: '/register', state: { selectedProductId: product.uid } }}/>;
    }
  }

  return (<Wrapper>
    <Header>
      {t('selectParkingProduct')}
    </Header>
    <FlexContainer>
      {applicableProducts.map(prod => <ParkingProduct
        key={prod.uid}
        prod={prod}
        disabled={kioskUtils.invalidProduct(prod) || !kioskState.connectionOk}
        onSelectProduct={handleProductSelection}
      />)}
    </FlexContainer>

    { kioskState.isLoading && <FillingSpinner />}
    <ButtonContainer>
      <BackOrLogoutButton
        kioskState={kioskState}
        onClose={onClose}
      />
    </ButtonContainer>

    <UnlockCodeModal
      visible={unlockModalVisible}
      onClose={handleUnlockClose}
      onSuccess={handleUnlockSuccess}
      productId={selectedProduct?.uid}
      connectionOk={kioskState.connectionOk}
    />
  </Wrapper>);
};

export default ParkingProductSelection;
